import { mergeAttributes } from '@tiptap/core';
import { Mention as BaseMention } from '@tiptap/extension-mention';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { NodeViewWrapper } from '@tiptap/react';
import type { SuggestionProps } from '@tiptap/suggestion';

import ReactRenderer from '../../../react/renderer';
import MentionsProvider from '../../../services/mentions-provider';
import { floatingRootFor } from '../../../utils/floating_root';
import { DropdownRef } from '../../Dropdown';
import MentionComponent, { MentionProps } from '../Mention';
import { LeafMentionSuggestion, MentionsList, type MentionsListProps, MentionSuggestion } from '../MentionsList';

let activeMentionMenuTeardown: (() => void) | null = null;

const ANCHOR_TOLERANCE_PX = 24;

const isFiniteRect = (rect: DOMRect | DOMRectReadOnly | null): rect is DOMRect | DOMRectReadOnly => {
  if (!rect) return false;

  return [rect.left, rect.top, rect.right, rect.bottom, rect.width, rect.height].every(Number.isFinite);
};

const isOriginRect = (rect: DOMRect | DOMRectReadOnly) => {
  return Math.abs(rect.left) <= 1 && Math.abs(rect.top) <= 1 && Math.abs(rect.width) <= 1 && Math.abs(rect.height) <= 1;
};

const isRectWithinBounds = (rect: DOMRect | DOMRectReadOnly, bounds: DOMRect | null) => {
  if (!bounds) return true;

  return (
    rect.left >= bounds.left - ANCHOR_TOLERANCE_PX &&
    rect.left <= bounds.right + ANCHOR_TOLERANCE_PX &&
    rect.top >= bounds.top - ANCHOR_TOLERANCE_PX &&
    rect.top <= bounds.bottom + ANCHOR_TOLERANCE_PX
  );
};

const getRectAnchorTop = (rect: DOMRect | DOMRectReadOnly) => {
  return rect.top + Math.max(rect.height, 0) / 2;
};

const mapSuggestionProps = (
  referenceElement: HTMLElement | null,
  props: SuggestionProps,
): Pick<MentionsListProps, 'referenceElement' | 'query'> => ({
  referenceElement: referenceElement ?? null,
  query: props.query,
});

const SERIALIZED_MENTION_REGEX = /\{\{\{(.*?)}}}/g;

const parseSerializedMention = (serializedFieldReference: string) => {
  const separatorIndex = serializedFieldReference.lastIndexOf('|');
  if (separatorIndex < 0) return null;

  const label = serializedFieldReference.slice(0, separatorIndex);
  const slug = serializedFieldReference.slice(separatorIndex + 1);

  if (!label || !slug) return null;

  return { label, slug };
};

const buildMentionFragmentFromText = (doc: Document, text: string) => {
  if (!text.includes('{{{')) return null;

  SERIALIZED_MENTION_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null = null;
  let lastIndex = 0;
  let hasMatch = false;
  const fragment = doc.createDocumentFragment();

  while ((match = SERIALIZED_MENTION_REGEX.exec(text)) !== null) {
    hasMatch = true;

    if (match.index > lastIndex) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex, match.index)));
    }

    const serializedFieldReference = match[1] ?? '';
    const parsed = parseSerializedMention(serializedFieldReference);

    if (!parsed) {
      fragment.appendChild(doc.createTextNode(match[0]));
    } else {
      const mention = doc.createElement('mention');
      mention.setAttribute('data-id', parsed.slug);
      mention.setAttribute('data-label', parsed.label);
      fragment.appendChild(mention);
    }

    lastIndex = match.index + match[0].length;
  }

  if (!hasMatch) return null;

  if (lastIndex < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
  }

  return fragment;
};

export const hasSerializedMentions = (content: string) => content.includes('{{{') && content.includes('}}}');

export const toSerializedMentionsUrlValidationValue = (content: string) =>
  content.replace(SERIALIZED_MENTION_REGEX, 'platform.test');

const countMentionNodes = (doc: ProseMirrorNode | null | undefined) => {
  if (!doc) return 0;

  let count = 0;

  doc.descendants(node => {
    if (node.type?.name === 'mention') {
      count += 1;
    }

    return true;
  });

  return count;
};

const hasSelectedMentionNode = (doc: ProseMirrorNode | null | undefined, from: number, to: number) => {
  if (!doc || from === to) return false;

  let hasSelectedMention = false;

  doc.descendants((node, pos) => {
    if (node.type?.name !== 'mention') return true;

    const nodeFrom = pos;
    const nodeTo = pos + node.nodeSize;

    if (nodeFrom < to && nodeTo > from) {
      hasSelectedMention = true;
      return false;
    }

    return true;
  });

  return hasSelectedMention;
};

export const serializeMentions = (content: string) => {
  const mentionTagRegex = /<mention\b([^>]*)><\/mention>/g;

  return content.replace(mentionTagRegex, (match, rawAttrs) => {
    const attrs = rawAttrs || '';
    const slug = attrs.match(/\bdata-id="([^"]*)"/)?.[1];
    const label = attrs.match(/\bdata-label="([^"]*)"/)?.[1];

    if (!slug || !label) return match;
    return `{{{${label}|${slug}}}}`;
  });
};

export const deserializeMentions = (content: string) => {
  if (!hasSerializedMentions(content)) return content;

  const container = document.createElement('div');
  container.innerHTML = content;

  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);

  let currentNode = walker.nextNode();
  while (currentNode) {
    if (currentNode instanceof Text) {
      textNodes.push(currentNode);
    }
    currentNode = walker.nextNode();
  }

  textNodes.forEach(textNode => {
    const parentNode = textNode.parentNode;
    const fragment = buildMentionFragmentFromText(textNode.ownerDocument || document, textNode.nodeValue ?? '');

    if (!parentNode || !fragment) return;

    parentNode.replaceChild(fragment, textNode);
  });

  return container.innerHTML;
};

export const build = (
  mentionSuggestions: MentionSuggestion[] | MentionsProvider,
  options: { temporal?: boolean; maxMentions?: number } = {},
  hook?: any,
) => {
  const mentionsProvider =
    mentionSuggestions instanceof MentionsProvider
      ? mentionSuggestions
      : new MentionsProvider(mentionSuggestions, options.temporal === undefined ? {} : { temporal: options.temporal });

  const Mention = BaseMention.extend({
    name: 'mention',
    atom: true,

    parseHTML() {
      return [
        {
          tag: 'mention',
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      return ['mention', mergeAttributes(HTMLAttributes)];
    },

    addNodeView() {
      return ({
        node: {
          attrs: { id, label },
        },
      }) => {
        const rootElement = document.createElement('span');
        const renderer = new ReactRenderer<any, MentionProps>(MentionComponent, {
          props: {
            id,
            label,
            mentionsProvider,
            wrapper: NodeViewWrapper,
            temporal: options.temporal,
          },
          renderOnInit: false,
          rootElement,
        });

        const renderNow = () => renderer.render();
        if (typeof queueMicrotask === 'function') {
          queueMicrotask(renderNow);
        } else {
          setTimeout(renderNow, 0);
        }

        return {
          dom: rootElement,
          destroy() {
            renderer.destroy();
          },
        };
      };
    },
  });

  return Mention.configure({
    deleteTriggerWithBackspace: true,
    suggestion: {
      char: '@',
      allowedPrefixes: [' ', '\u00A0'],
      allowSpaces: true,
      allow: ({ state }) => {
        if (!options.maxMentions || options.maxMentions < 1) return true;

        if (countMentionNodes(state.doc) < options.maxMentions) return true;

        const { from, to } = state.selection;
        return hasSelectedMentionNode(state.doc, from, to);
      },

      render: () => {
        let renderer: ReactRenderer<DropdownRef, MentionsListProps> | undefined;
        let currentRange: SuggestionProps['range'] | null = null;
        let latestProps: SuggestionProps | null = null;
        let fallbackReferenceElement: HTMLElement | null = null;

        // Fluxon dialogs handle Escape in the capture phase at `document`, before the editor
        // sees the key, so Escape with the menu open also closed the modal around the editor and
        // discarded unsaved edits. Claim the key first while the menu is open and close only the
        // menu, as onKeyDown below does: the suggestion stays active, so the menu stays closed
        // until a new `@`.
        const onEscape = (event: KeyboardEvent) => {
          const { target } = event;
          if (event.key !== 'Escape' || event.isComposing || !(target instanceof Element)) return;
          if (!latestProps?.editor.view.dom.contains(target) && !target.closest('.mention-menu')) return;

          event.preventDefault();
          event.stopPropagation();
          teardownRenderer();
        };

        const teardownRenderer = () => {
          window.removeEventListener('keydown', onEscape, true);
          renderer?.destroy();
          renderer = undefined;
          currentRange = null;
          latestProps = null;
          if (fallbackReferenceElement?.parentNode) {
            fallbackReferenceElement.parentNode.removeChild(fallbackReferenceElement);
          }
          fallbackReferenceElement = null;
          if (activeMentionMenuTeardown === teardownRenderer) {
            activeMentionMenuTeardown = null;
          }
        };

        const updateFallbackReferenceElement = (rect: DOMRect | DOMRectReadOnly) => {
          if (!fallbackReferenceElement) {
            fallbackReferenceElement = document.createElement('span');
            fallbackReferenceElement.style.position = 'fixed';
            fallbackReferenceElement.style.pointerEvents = 'none';
            fallbackReferenceElement.style.width = '0px';
            fallbackReferenceElement.style.height = '0px';
            document.body.appendChild(fallbackReferenceElement);
          }

          fallbackReferenceElement.style.left = `${rect.left}px`;
          fallbackReferenceElement.style.top = `${getRectAnchorTop(rect)}px`;

          return fallbackReferenceElement;
        };

        const resolveReferenceElement = (props: SuggestionProps) => {
          const editorElement = props.editor?.view?.dom;
          const editorBounds =
            editorElement instanceof HTMLElement && editorElement.isConnected
              ? editorElement.getBoundingClientRect()
              : null;
          const rect = props.clientRect?.() ?? null;

          if (isFiniteRect(rect) && !isOriginRect(rect) && isRectWithinBounds(rect, editorBounds)) {
            return updateFallbackReferenceElement(rect);
          }

          if (props.decorationNode instanceof HTMLElement && props.decorationNode.isConnected) {
            const decorationBounds = props.decorationNode.getBoundingClientRect();
            if (
              isFiniteRect(decorationBounds) &&
              !isOriginRect(decorationBounds) &&
              isRectWithinBounds(decorationBounds, editorBounds)
            ) {
              return updateFallbackReferenceElement(decorationBounds);
            }
          }

          return null;
        };

        // Set by SuperText when a `mentions_source` module backs this editor.
        const serverSearchEnabled = () => {
          const target = hook?.thisTarget;
          if (target == null) return false;

          return (
            document
              .querySelector(`[data-supertext-target="${target}"]`)
              ?.getAttribute('data-mention-server-search') === 'true'
          );
        };

        const requestSuggestions = (
          event: string,
          payload: object,
          onSuggestions: (suggestions: MentionSuggestion[] | null, reply?: any) => void,
        ) => {
          const target = hook?.pushEventTo ? hook.thisTarget : null;
          if (!target) return false;

          hook.pushEventTo(target, event, payload, (reply: any) => {
            if (!reply || !reply.mention_suggestions) {
              onSuggestions(null, reply);
              return;
            }

            try {
              onSuggestions(JSON.parse(reply.mention_suggestions), reply);
            } catch (e) {
              console.error('Failed to parse mention suggestions:', e);
              onSuggestions(null, reply);
            }
          });

          return true;
        };

        return {
          onStart: props => {
            if (activeMentionMenuTeardown && activeMentionMenuTeardown !== teardownRenderer) {
              activeMentionMenuTeardown();
            }

            teardownRenderer();
            latestProps = props;
            const referenceElement = resolveReferenceElement(props);
            renderer = new ReactRenderer<DropdownRef, MentionsListProps>(MentionsList, {
              props: {
                ...mapSuggestionProps(referenceElement, props),
                portalRoot: floatingRootFor(props.editor.view.dom),
                mentionsProvider,
                onDropdownOpen: (key: string) => {
                  // Set loading state and request data from backend
                  mentionsProvider.setLoadingState(key, true);

                  if (hook && hook.pushEventTo) {
                    const target = hook.thisTarget;
                    if (!target) {
                      mentionsProvider.setLoadingState(key, false);
                      return;
                    }

                    hook.pushEventTo(target, 'load_more', { key }, (reply: any) => {
                      if (reply && reply.mention_suggestions) {
                        try {
                          const newSuggestions = JSON.parse(reply.mention_suggestions);
                          mentionsProvider.updateSuggestions(newSuggestions, key);
                        } catch (e) {
                          console.error('Failed to parse mention suggestions:', e);
                          mentionsProvider.setLoadingState(key, false);
                        }
                      } else {
                        mentionsProvider.setLoadingState(key, false);
                      }
                    });
                  } else {
                    mentionsProvider.setLoadingState(key, false);
                  }
                },
                serverSearch: serverSearchEnabled(),
                onLoadMore: (key: string, offset: number) => {
                  const sent = requestSuggestions('load_more', { key, offset }, (suggestions, reply) => {
                    if (!suggestions) {
                      mentionsProvider.setLoadingState(key, false);
                      return;
                    }

                    mentionsProvider.appendSuggestions(suggestions, key, {
                      hasMore: Boolean(reply?.has_more),
                      nextOffset: reply?.next_offset,
                    });
                  });

                  // Nothing will come back, so let the button drop out of its pending state.
                  if (!sent) mentionsProvider.setLoadingState(key, false);
                },
                onSearch: (query: string, onReply) => {
                  const sent = requestSuggestions('search_mentions', { query }, suggestions =>
                    onReply(suggestions ?? []),
                  );

                  if (!sent) onReply([]);
                },
                onSelect: (suggestion: LeafMentionSuggestion) => {
                  const range = currentRange ?? latestProps?.range;
                  const editor = latestProps?.editor ?? props.editor;
                  if (!range || !editor) return;
                  if (options.maxMentions && options.maxMentions > 0) {
                    const currentMentions = countMentionNodes(editor.state.doc);

                    if (currentMentions >= options.maxMentions) {
                      teardownRenderer();
                      return;
                    }
                  }

                  editor
                    .chain()
                    .focus()
                    .insertContentAt(range, [
                      {
                        type: 'mention',
                        attrs: { id: suggestion.slug, label: suggestion.label },
                      },
                    ])
                    .run();

                  teardownRenderer();
                },
              },
              as: 'div',
            });

            currentRange = props.range;
            activeMentionMenuTeardown = teardownRenderer;
            window.addEventListener('keydown', onEscape, true);
          },

          onUpdate: props => {
            latestProps = props;
            currentRange = props.range;
            const referenceElement = resolveReferenceElement(props);
            renderer?.updateProps(mapSuggestionProps(referenceElement, props));
          },

          onKeyDown(props) {
            if (props.event.key === 'Escape') {
              teardownRenderer();

              return true;
            }

            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(props.event.key)) {
              renderer?.ref?.onKeyDown(props.event);

              return true;
            }

            return false;
          },

          onExit() {
            teardownRenderer();
          },
        };
      },
    },
  });
};

export default {
  build,
  serializeMentions,
  deserializeMentions,
  hasSerializedMentions,
  toSerializedMentionsUrlValidationValue,
};
